import { Divider, IconButton, Sheet, Tooltip, Typography } from "@mui/joy";
import { Box } from "@mui/system";
import moment from "moment";
import { useEffect, useState } from "react";
import { Timer } from "../entities";
import { KronosTimerDuration } from "./kronosTimerDuration";
import { KronosTimerString } from "./kronosTimerString";
import { ProjectAutocomplete } from "./project/projectAutocomplete";
import { TaskAutocomplete } from "./task/taskAutocomplete";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TextField } from "@mui/material";

/**
 *
 * @returns {ReactElement} KronosTimerForm page
 */
export function KronosTimerForm({ timer, onChange }: KronosTimerPopperType) {
  const [_timer, setTimer] = useState(timer);
  const [_projectID, setProjectID] = useState<number | undefined>();
  const [_taskID, setTaskID] = useState<number | undefined>();

  useEffect(() => {
    if (!open) {
      setTaskID(undefined);
      setProjectID(undefined);
    }
  }, [open]);

  useEffect(() => {
    setTimer(timer);
  }, [timer]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Sheet
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          width: 400,
          alignItems: "start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          {!timer?.completed() ? (
            <Tooltip
              size="sm"
              title={timer?.start() ? "Stop timer" : "Start timer"}
            >
              <IconButton
                variant={"solid"}
                color={timer ? "danger" : "neutral"}
                disabled={!timer?.start() && !_projectID}
                size="lg"
                onClick={() => {
                  const _nt: Partial<Timer> = {
                    projectID: _projectID,
                    taskID: _taskID,
                    ..._timer,
                  };
                  if (!_nt.startDateUTC) {
                    _nt.startDateUTC = moment().utc().format();
                  } else {
                    _nt.endDateUTC = moment().utc().format();
                  }

                  onChange(_nt);
                }}
              >
                <i
                  className={
                    timer?.start() ? "fa-solid fa-stop" : "fa-solid fa-play"
                  }
                ></i>
              </IconButton>
            </Tooltip>
          ) : (
            []
          )}
          <Box>
            <Typography level="body1">
              <b>Project Name</b>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          {timer?.completed() ? (
            <KronosTimerDuration timer={timer} />
          ) : (
            <KronosTimerString startDateUTC={timer?.start()} />
          )}
          {timer?.start() ? (
            <Typography level="body3">
              {moment(timer?.start()).format("lll")}
            </Typography>
          ) : (
            []
          )}
        </Box>
      </Sheet>
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <ProjectAutocomplete
          projectID={_timer?.projectID ?? _projectID}
          size="small"
          onChange={(e) => {
            setProjectID(e?.id);
            if (_timer?.id) onChange({ ...timer, projectID: e?.id });
          }}
          helperText={"Required"}
        />
        <TaskAutocomplete
          taskID={_timer?.taskID ?? _taskID}
          size="small"
          onChange={(e) => {
            setTaskID(e?.id);
            if (_timer?.id) onChange({ ...timer, taskID: e?.id });
          }}
        />
        {_timer?.id ? (
          <DateTimePicker
            label="Start Date"
            value={_timer?.startDateUTC ? moment(_timer?.startDateUTC) : null}
            onChange={(d) => {
              onChange({ ...timer, startDateUTC: d?.utc().format() });
            }}
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" />
            )}
          />
        ) : (
          []
        )}
        {_timer?.id && _timer.completed() ? (
          <DateTimePicker
            label="End Date"
            value={_timer?.endDateUTC ? moment(_timer?.endDateUTC) : null}
            onChange={(d) => {
              onChange({ ...timer, endDateUTC: d?.utc().format() });
            }}
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" />
            )}
          />
        ) : (
          []
        )}
      </Box>
    </LocalizationProvider>
  );
}

type KronosTimerPopperType = {
  timer?: Timer;
  onChange: (t: Partial<Timer>) => void;
};
